import { FileUtils } from 'dy-frontend-shared/lib/utils';
import React, { useEffect, useState } from 'react';

import Box from '@app/components/Box';
import Heading from '@app/components/Heading';
import NonIdealState from '@app/components/NonIdealState';
import { Card as BlueprintjsCard, Button, Colors, Icon, Popover } from '@blueprintjs/core';
import Flex from '@components/Flex';
import IconTextBlock from '@components/IconTextBlock';
import Image from '@components/Image';
import Overlay from '@components/Overlay';
import PdfViewer from '@components/PdfViewer';
import DevText from '@components/Text';
import VideoViewer from '@components/VideoViewer';
import { ModalProps } from '@modals/types';

import {
    Card,
    DismissModalPanel,
    ImageViewerWrapper,
    ModalContent,
    PdfViewerWrapper,
    VideoViewerWrapper,
    ViewerWrapper,
} from './styled';
import { FileViewerItem } from './types';

export interface FileViewerModalProps {
    currentFileIndex: number;
    files: FileViewerItem[];
}

type Props = ModalProps<FileViewerModalProps>;

const FileViewerModal: React.FC<Props> = ({ closeModal, data }) => {
    const [currentFileItemIndex, setCurrentFileItemIndex] = useState(data ? data.currentFileIndex : 0);

    const handleEscapeKeyPressed = (e: KeyboardEvent) => {
        if (e.code === 'Escape') {
            closeModal?.();
        }
    };

    const handleCloseModal = () => {
        closeModal?.();
    };

    useEffect(() => {
        document.addEventListener('keydown', handleEscapeKeyPressed);
        return () => {
            document.removeEventListener('keydown', handleEscapeKeyPressed);
        };
        // eslint-disable-next-line
    }, []);

    if (!data || data.files.length === 0) {
        // Data was NOT provided or files is empty array, meaning nothing to view
        closeModal?.();
        return null;
    }

    const renderFileViewerFileItem = (file: FileViewerItem) => {
        const { url, downloadableUrl, extension, name } = file;

        // Get first & last first/last item flag for current file item
        const isCurrentFileItemFirst = currentFileItemIndex === 0;
        const isCurrentFileItemLast = currentFileItemIndex === data.files.length - 1;

        const handleGoToPreviousFileItem = () => {
            if (isCurrentFileItemFirst) {
                // First element
                setCurrentFileItemIndex(data.files.length - 1);
                return;
            }

            setCurrentFileItemIndex((i) => i - 1);
        };

        const handleGoToNextFileItem = () => {
            if (isCurrentFileItemLast) {
                // Last element
                setCurrentFileItemIndex(0);
                return;
            }

            setCurrentFileItemIndex((i) => i + 1);
        };

        const renderViewer = () => {
            let viewer = <></>;

            const isFileItemImage = FileUtils.isImage(extension);
            const isFileItemPdf = FileUtils.isPdf(extension);
            const isFileItemVideo = FileUtils.isVideo(extension);
            const isViewerExistForFileItem = [isFileItemImage, isFileItemPdf, isFileItemVideo].some((flag) => flag);

            if (!isViewerExistForFileItem) {
                // No viewer
                viewer = (
                    <Flex fullWidth fullHeight align="center" justify="center">
                        <NonIdealState
                            className="mt-2"
                            icon={<Icon className="mb-2" icon="box" size={70} />}
                            title={
                                <Heading type="h4" className="mb-1">
                                    {file.name}
                                    {!!file.extension && `.${file.extension}`}
                                </Heading>
                            }
                            description={
                                <DevText muted>File could not be viewed, you can download it on your computer</DevText>
                            }
                            action={
                                <Button className="mt-2" icon="import" onClick={() => window.open(downloadableUrl)}>
                                    Download
                                </Button>
                            }
                        />
                    </Flex>
                );
            } else {
                if (isFileItemImage) {
                    viewer = (
                        <ImageViewerWrapper>
                            <Image objectFit="contain" src={url} alt={name} />
                        </ImageViewerWrapper>
                    );
                } else if (isFileItemPdf) {
                    viewer = (
                        <PdfViewerWrapper>
                            <PdfViewer src={url} />
                        </PdfViewerWrapper>
                    );
                } else if (isFileItemVideo) {
                    viewer = (
                        <VideoViewerWrapper>
                            <VideoViewer withAspectRatio={false} url={url} />
                        </VideoViewerWrapper>
                    );
                }
            }

            // TODO: can't view viewer

            return <ViewerWrapper className="custom-thin-scroll">{viewer}</ViewerWrapper>;
        };

        const renderPreviousFileItemButton = () => {
            return <Button minimal icon="chevron-left" onClick={handleGoToPreviousFileItem} />;
        };

        const renderFileItemIndexCounter = () => {
            return (
                <DevText>
                    {currentFileItemIndex + 1} / {data.files.length}
                </DevText>
            );
        };

        const renderNextFileItemButton = () => {
            return <Button minimal icon="chevron-right" onClick={handleGoToNextFileItem} />;
        };

        const renderFileItemIndexControls = () => {
            if (data.files.length <= 1) {
                return null;
            }

            return (
                <Flex className="ml-1" direction="row" align="center">
                    <div className="mr-1">{renderPreviousFileItemButton()}</div>
                    <div className="mr-1">{renderFileItemIndexCounter()}</div>
                    <div>{renderNextFileItemButton()}</div>
                </Flex>
            );
        };

        const renderCopyFileItemSrcButton = () => {
            return (
                <Popover
                    content={
                        <BlueprintjsCard compact>
                            <DevText>Copied</DevText>
                        </BlueprintjsCard>
                    }
                >
                    <Button minimal icon="duplicate" onClick={() => navigator.clipboard.writeText(file.url)}>
                        Copy link
                    </Button>
                </Popover>
            );
        };

        const renderDownloadFileItemButton = () => {
            return (
                <Button minimal icon="import" onClick={() => window.open(file.downloadableUrl)}>
                    Download
                </Button>
            );
        };

        const renderFooter = () => {
            return (
                <Box width="100%" padding="8px" backgroundColor={Colors.DARK_GRAY1}>
                    <Flex justify="space-between" align="center">
                        <Flex direction="row" align="center">
                            <IconTextBlock className="mr-2" icon="document" text={extension} />
                            <Flex direction="column" justify="center">
                                <DevText>{file.name}</DevText>
                                <DevText muted>{FileUtils.getFormattedFileSize(file.size)}</DevText>
                            </Flex>
                        </Flex>

                        <Flex direction="row" align="center">
                            <div className="mr-1">{renderCopyFileItemSrcButton()}</div>
                            {renderDownloadFileItemButton()}
                            {renderFileItemIndexControls()}
                        </Flex>
                    </Flex>
                </Box>
            );
        };

        return (
            <Card onClick={(e) => e.stopPropagation()}>
                <Flex fullWidth fullHeight direction="column" align="flex-start" justify="flex-start">
                    {renderViewer()}
                    {renderFooter()}
                </Flex>
            </Card>
        );
    };

    const renderDismissModalPanel = () => {
        return (
            <DismissModalPanel onClick={handleCloseModal}>
                <Flex fullHeight fullWidth align="center" justify="center">
                    <Icon className="mr-1" icon="chevron-up" />
                    <DevText>Click here to close</DevText>
                    <Icon className="ml-1" icon="chevron-up" />
                </Flex>
            </DismissModalPanel>
        );
    };

    // Get current file item
    const currentFileItem = data.files[currentFileItemIndex];

    return (
        <Overlay isOpen noPadding height="100%" onClose={closeModal}>
            <ModalContent>
                {renderDismissModalPanel()}
                {renderFileViewerFileItem(currentFileItem)}
            </ModalContent>
        </Overlay>
    );
};

export default FileViewerModal;
